import { useEffect, useRef, useState } from "react";
import InlineTitleEditor from "../InlineTitleEditor";
import styles from "./ConversationRow.module.scss";

const LONG_PRESS_MS = 500;

const ConversationRow = ({
  conversation,
  isActive,
  isRenaming,
  renameDraft,
  onRenameDraftChange,
  onCommitRename,
  onCancelRename,
  onStartRename,
  maxTitleLength,
  onSelect,
  onDelete,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const rowRef = useRef(null);
  const longPressTimerRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleOutside = (e) => {
      if (!rowRef.current?.contains(e.target)) setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => () => clearTimeout(longPressTimerRef.current), []);

  const title = conversation.title || "Untitled Chat";

  const beginRename = () => {
    setIsMenuOpen(false);
    onStartRename(conversation.id, conversation.title || "");
  };

  const startLongPress = () => {
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(beginRename, LONG_PRESS_MS);
  };
  const cancelLongPress = () => clearTimeout(longPressTimerRef.current);

  if (isRenaming)
    return (
      <div
        className={`${styles.row} ${isActive ? styles["row--active"] : ""}`}
        ref={rowRef}
      >
        <InlineTitleEditor
          value={renameDraft}
          onChange={onRenameDraftChange}
          onCommit={onCommitRename}
          onCancel={onCancelRename}
          maxLength={maxTitleLength}
        />
      </div>
    );

  return (
    <div
      ref={rowRef}
      className={`${styles.row} ${isActive ? styles["row--active"] : ""}`}
      onClick={() => onSelect(conversation.id)}
    >
      <span
        className={styles.row__title}
        onDoubleClick={(e) => {
          e.stopPropagation();
          beginRename();
        }}
        onTouchStart={startLongPress}
        onTouchEnd={cancelLongPress}
        onTouchMove={cancelLongPress}
        title={title}
      >
        {title}
      </span>

      <button
        className={styles.row__kebab}
        onClick={(e) => {
          e.stopPropagation();
          setIsMenuOpen((prev) => !prev);
        }}
        title="Conversation options"
        aria-label="Conversation options"
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.7" />
          <circle cx="12" cy="12" r="1.7" />
          <circle cx="12" cy="19" r="1.7" />
        </svg>
      </button>

      {isMenuOpen && (
        <div className={styles.row__menu} role="menu">
          <button
            onClick={(e) => {
              e.stopPropagation();
              beginRename();
            }}
          >
            Rename
          </button>
          <button
            className={styles["row__menu--danger"]}
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onDelete(conversation.id);
            }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default ConversationRow;
